import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Alert,
    Divider,
    IconButton,
    Typography,
    Card,
    CardContent,
    Button,
    Stack,
    Avatar,
    Chip
} from '@mui/material';
import {
    Store as StoreIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Business as BusinessIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticService } from '../../Commun/services/blobticService';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';

export interface AgencyFormData {
    // Primary Key
    DOS_IDF?: string;

    // Main Fields
    DOS_CODE: string;
    DOS_LIB: string;
    DOS_EMAIL?: string;
    DOS_TELPH?: string;
    DOS_TELCP?: string;
    DOS_ADR1?: string;
    DOS_ADR2?: string;
    DOS_MMO?: string;
    DOS_PAYS_LEGAL?: string;

    // Fiscal Identifiers
    DOS_NIF?: string;
    DOS_NIS?: string;
    DOS_RC?: string;
    DOS_AI?: string;

    // Media
    DOS_LOGO?: string;

    // Date Fields
    DOS_START_DATE?: string;
    DOS_END_DATE?: string;
    DOS_CLOSEDPLNG?: string;

    // Hidden / Audit
    DOS_UIDF?: string;
    LAST_UPDT?: string;
    USER_LIB?: string;
}

const initialForm: AgencyFormData = {
    DOS_CODE: '',
    DOS_LIB: ''
};

const AgencyPage: React.FC = () => {
    const { t } = useLanguage();

    // View state: 'list' (default) or 'form' (new/edit)
    const [view, setView] = useState<'list' | 'form'>('list');

    // List view mode: 'grid' or 'table' (initialized from user default preference)
    const [viewMode, setViewMode] = useDefaultViewMode();

    // Agencies list
    const [agenciesList, setAgenciesList] = useState<AgencyFormData[]>([]);
    const [listLoading, setListLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState<AgencyFormData>(initialForm);
    const [error, setError] = useState<string | null>(null);

    // Load agencies from IndexedDB cache / Server DB
    const loadAgencies = async (forceRefresh: boolean = false) => {
        setListLoading(true);
        try {
            const response = await blobticService.executeZoomRequest<AgencyFormData[]>({
                ZoomKey: 'agency_list',
                Action: {
                    OKey: 'Blobtic_Dossier,dossier,select'
                }
            }, !forceRefresh);

            if (response.success && Array.isArray(response.data)) {
                setAgenciesList(response.data);
            }
        } catch (err: any) {
            console.error('Error loading agencies', err);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        loadAgencies();
        // Reactive sync with IndexedDB / WebSocket mutations
        const unsubscribe = BlobticDataManager.subscribe((obj) => {
            if (obj === 'dossier') {
                loadAgencies();
            }
        });
        return () => unsubscribe();
    }, []);

    // ── Navigation & Actions ───────────────────────────────────────────────────

    const openNew = () => {
        setFormData(initialForm);
        setError(null);
        setView('form');
    };

    const openEdit = (agency: AgencyFormData) => {
        setFormData({ ...agency });
        setError(null);
        setView('form');
    };

    const backToList = () => {
        setView('list');
        setFormData(initialForm);
        setError(null);
        loadAgencies();
    };

    const handleDelete = async (agency: AgencyFormData) => {
        if (!agency.DOS_IDF) return;

        const confirmed = await blobticMessage.confirm({
            title: t('common.confirm.title.delete'),
            message: t('page.agency.confirm_delete', agency.DOS_LIB || agency.DOS_CODE),
            severity: 'warning',
            confirmText: t('common.actions.confirm'),
            cancelText: t('btn.cancel')
        });

        if (!confirmed) return;

        try {
            const res = await blobticService.executeZoomRequest({
                ZoomKey: 'agency_delete',
                Action: {
                    OKey: 'Blobtic_Dossier,dossier,delete',
                    DOS_IDF: agency.DOS_IDF
                }
            }, false);

            if (res.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadAgencies();
            } else {
                blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
            }
        } catch (e: any) {
            blobticMessage.error(t('common.error'), e.message);
        }
    };

    // ── Form Handlers ──────────────────────────────────────────────────────────

    const handleInputChange = (field: keyof AgencyFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClear = () => {
        setFormData(initialForm);
        setError(null);
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError(t('page.agency.validation.image_size') || 'L\'image doit faire moins de 10MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                handleInputChange('DOS_LOGO', reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        handleInputChange('DOS_LOGO', '');
    };

    const validate = () => {
        if (!formData.DOS_CODE || !formData.DOS_LIB) {
            return t('page.agency.validation.required') || 'Code and Name are required.';
        }
        return null;
    };

    // ─── LIST VIEW ─────────────────────────────────────────────────────────────

    if (view === 'list') {
        return (
            <GenericPage
                title={t('page.agency.title') || "Agences"}
                icon={<BusinessIcon />}
                footerEnabled={false}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onNew={openNew}
                newButtonLabel={t('page.agency.add_new') || "Nouvelle Agence"}
            >
                {listLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">Chargement en cours...</Typography>
                    </Box>
                ) : viewMode === 'grid' ? (
                    /* ── GRID VIEW ── */
                    <Grid container spacing={2.5}>
                        {agenciesList.map(agency => {
                            const closedPlngDate = agency.DOS_CLOSEDPLNG ? agency.DOS_CLOSEDPLNG.split('T')[0] : '';
                            const isClosed = Boolean(closedPlngDate);

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={agency.DOS_IDF || agency.DOS_CODE}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 3,
                                            borderColor: isClosed ? 'warning.light' : 'divider',
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.2s',
                                            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                            {/* Header with Logo / Code / Name */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                {agency.DOS_LOGO ? (
                                                    <Avatar
                                                        src={agency.DOS_LOGO}
                                                        variant="rounded"
                                                        sx={{ width: 48, height: 48, borderRadius: 2 }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        variant="rounded"
                                                        sx={{ width: 48, height: 48, bgcolor: 'primary.light', borderRadius: 2 }}
                                                    >
                                                        <StoreIcon color="primary" />
                                                    </Avatar>
                                                )}
                                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                                        {agency.DOS_LIB}
                                                    </Typography>
                                                    <Chip
                                                        label={agency.DOS_CODE}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1.5 }} />

                                            {/* Contact Details */}
                                            <Stack spacing={1} sx={{ mb: 2 }}>
                                                {agency.DOS_EMAIL && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {agency.DOS_EMAIL}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {agency.DOS_TELPH && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {agency.DOS_TELPH}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {agency.DOS_ADR1 && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {agency.DOS_ADR1}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Stack>

                                            {/* Closure & Date Badges */}
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                                {agency.DOS_START_DATE && (
                                                    <Chip
                                                        icon={<CalendarIcon sx={{ '&&': { fontSize: 13 } }} />}
                                                        label={`Début: ${agency.DOS_START_DATE.split('T')[0]}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                )}
                                                {isClosed && (
                                                    <Chip
                                                        icon={<LockIcon sx={{ '&&': { fontSize: 13, color: 'white' } }} />}
                                                        label={`Clôturé: ${closedPlngDate}`}
                                                        size="small"
                                                        sx={{ fontSize: '0.7rem', bgcolor: '#64748b', color: 'white', fontWeight: 'bold' }}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>

                                        {/* Card Actions */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2, gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => openEdit(agency)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(agency)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    /* ── TABLE VIEW ── */
                    <GenericTable
                        columns={[
                            { id: 'DOS_CODE', label: t('page.agency.field.code') || 'Code', minWidth: 100 },
                            { id: 'DOS_LIB', label: t('page.agency.field.lib') || 'Nom Agence', minWidth: 180 },
                            { id: 'DOS_TELPH', label: t('page.agency.field.phone') || 'Téléphone', minWidth: 130 },
                            { id: 'DOS_EMAIL', label: t('page.agency.field.email') || 'Email', minWidth: 160 },
                            { id: 'DOS_ADR1', label: t('page.agency.field.address1') || 'Adresse', minWidth: 180 },
                            {
                                id: 'DOS_START_DATE',
                                label: t('page.agency.field.start_date') || 'Date Début',
                                minWidth: 120,
                                format: (val: any) => val ? String(val).split('T')[0] : '-'
                            },
                            {
                                id: 'DOS_END_DATE',
                                label: t('page.agency.field.end_date') || 'Date Fin',
                                minWidth: 120,
                                format: (val: any) => val ? String(val).split('T')[0] : '-'
                            },
                            {
                                id: 'DOS_CLOSEDPLNG',
                                label: t('page.agency.field.closed_plng') || 'Clôture Planning',
                                minWidth: 140,
                                format: (val: any) => val ? (
                                    <Chip
                                        icon={<LockIcon sx={{ '&&': { fontSize: 13, color: 'white' } }} />}
                                        label={String(val).split('T')[0]}
                                        size="small"
                                        sx={{ bgcolor: '#64748b', color: 'white', fontWeight: 'bold' }}
                                    />
                                ) : '-'
                            }
                        ]}
                        data={agenciesList}
                        onEdit={(row) => openEdit(row as AgencyFormData)}
                        onDelete={(row) => handleDelete(row as AgencyFormData)}
                        filterColumns={['DOS_CODE', 'DOS_LIB']}
                        searchPlaceholder={t('page.agency.search_placeholder') || "Rechercher une agence..."}
                        maxHeight="calc(100vh - 340px)"
                    />
                )}
            </GenericPage>
        );
    }

    // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

    const isEditing = Boolean(formData.DOS_IDF);

    const footerInfo = {
        lastUpdate: formData.LAST_UPDT ? new Date(formData.LAST_UPDT).toLocaleString() : '-',
        userLib: formData.USER_LIB || '-'
    };

    return (
        <GenericPage<AgencyFormData>
            title={
                isEditing
                    ? (t('page.agency.edit_title', formData.DOS_LIB || formData.DOS_CODE) || `Modifier l'agence : ${formData.DOS_LIB || formData.DOS_CODE}`)
                    : (t('page.agency.new_title') || "Nouvelle Agence")
            }
            icon={<BusinessIcon />}
            footerEnabled={true}
            footerInfo={footerInfo}
            additionalActions={
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={backToList}
                >
                    {t('page.agency.back_to_list') || "Retour à la liste"}
                </Button>
            }
            crudConfig={{
                entityKey: 'Blobtic_Dossier,dossier',
                primaryKey: 'DOS_IDF'
            }}
            data={formData}
            onDataChange={(data) => {
                setFormData(data);
                loadAgencies();
            }}
            onValidate={validate}
            onClear={handleClear}
        >
            <Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.code') || "Code Agence"}
                            value={formData.DOS_CODE}
                            onChange={(e) => handleInputChange('DOS_CODE', e.target.value)}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.lib') || "Nom Agence"}
                            value={formData.DOS_LIB}
                            onChange={(e) => handleInputChange('DOS_LIB', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.email') || "Email"}
                            value={formData.DOS_EMAIL || ''}
                            onChange={(e) => handleInputChange('DOS_EMAIL', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.phone') || "Téléphone"}
                            value={formData.DOS_TELPH || ''}
                            onChange={(e) => handleInputChange('DOS_TELPH', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={8}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.address1') || "Adresse 1"}
                            value={formData.DOS_ADR1 || ''}
                            onChange={(e) => handleInputChange('DOS_ADR1', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={8}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.address2') || "Adresse 2"}
                            value={formData.DOS_ADR2 || ''}
                            onChange={(e) => handleInputChange('DOS_ADR2', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.start_date') || "Date Début"}
                            type="date"
                            value={formData.DOS_START_DATE ? formData.DOS_START_DATE.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('DOS_START_DATE', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.end_date') || "Date Fin"}
                            type="date"
                            value={formData.DOS_END_DATE ? formData.DOS_END_DATE.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('DOS_END_DATE', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.closed_plng') || "Date Clôture Planning / Agence"}
                            type="date"
                            value={formData.DOS_CLOSEDPLNG ? formData.DOS_CLOSEDPLNG.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('DOS_CLOSEDPLNG', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText={t('page.agency.helper.closed_plng') || "Verrouille le planning et les factures antérieurs ou égaux à cette date"}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={8}>
                        <TextField
                            fullWidth
                            label={t('page.agency.field.memo') || "Commentaire"}
                            multiline
                            rows={3}
                            value={formData.DOS_MMO || ''}
                            onChange={(e) => handleInputChange('DOS_MMO', e.target.value)}
                        />
                    </Grid>

                    {/* Fiscal Identifiers Section */}
                    <Grid item xs={12} md={8} lg={8}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    {t('page.agency.fiscal_identifiers') || "Identifiants Fiscaux & Légaux"}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.nif') || "NIF"}
                                            value={formData.DOS_NIF || ''}
                                            onChange={(e) => handleInputChange('DOS_NIF', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.nis') || "NIS"}
                                            value={formData.DOS_NIS || ''}
                                            onChange={(e) => handleInputChange('DOS_NIS', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.rc') || "Registre de Commerce (RC)"}
                                            value={formData.DOS_RC || ''}
                                            onChange={(e) => handleInputChange('DOS_RC', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.ai') || "Article d'Imposition (AI)"}
                                            value={formData.DOS_AI || ''}
                                            onChange={(e) => handleInputChange('DOS_AI', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.legal_country') || "Pays Légal"}
                                            value={formData.DOS_PAYS_LEGAL || ''}
                                            onChange={(e) => handleInputChange('DOS_PAYS_LEGAL', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('page.agency.field.mobile_phone') || "Téléphone Portable"}
                                            value={formData.DOS_TELCP || ''}
                                            onChange={(e) => handleInputChange('DOS_TELCP', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Logo Section */}
                    <Grid item xs={12} md={4} lg={4}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom align="center">
                                {t('page.agency.field.logo') || "Logo de l'Agence"}
                            </Typography>
                            {formData.DOS_LOGO ? (
                                <Box sx={{ position: 'relative', width: 140, height: 140, mb: 1 }}>
                                    <img
                                        src={formData.DOS_LOGO}
                                        alt="Logo Preview"
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={handleRemoveLogo}
                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1 }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: 140,
                                        height: 140,
                                        border: '2px dashed #ccc',
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1,
                                        bgcolor: 'background.default'
                                    }}
                                >
                                    <BusinessIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                    <Typography variant="caption" color="text.secondary" align="center">
                                        {t('page.agency.no_logo') || "Aucun logo"}
                                    </Typography>
                                </Box>
                            )}
                            <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                            >
                                {t('page.agency.upload_logo') || "Charger Logo"}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                />
                            </Button>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </GenericPage>
    );
};

export default AgencyPage;
